import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle, TrendingUp, TrendingDown } from 'lucide-react';
import { v3Api } from '../../hooks/useApi';
import { formatMoney } from '../../Shared/Formatters';
import { HEALTH } from './themes';

/**
 * Twin3DPanel — bento KPI popup for the selected unit.
 *
 * Ports the SalesPanel layout from the Claude Design handoff. Data source is
 * the existing `GET /api/twin/:propertyId/unit/:unitId` endpoint. Fields the
 * backend still returns as placeholders (kpi_history, lease, recent_transactions)
 * fall back to '—' so the layout never breaks.
 */

const fmtPct = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : `${Number(v).toFixed(1)}%`);
const fmtInt = (n) => (n == null ? '—' : Math.round(Number(n)).toLocaleString('en-US'));
// Tenant display currency via the shared formatter; 0 renders as '—' (empty unit).
const fmtSales = (v) => (
    v == null || Number.isNaN(Number(v)) || Number(v) === 0 ? '—' : formatMoney(v)
);

function Sparkline({ data, positive }) {
    if (!Array.isArray(data) || data.length < 2) {
        return <div className="twin3d-spark-empty">{/* no history yet */}</div>;
    }
    const w = 220, h = 56, pad = 4;
    const min = Math.min(...data), max = Math.max(...data);
    const span = max - min || 1;
    const pts = data.map((v, i) => {
        const x = pad + (i / (data.length - 1)) * (w - pad * 2);
        const y = h - pad - ((v - min) / span) * (h - pad * 2);
        return [x, y];
    });
    const line = pts.map((p) => p.join(',')).join(' ');
    const col = positive ? '#34d399' : '#f87171';
    return (
        <svg className="twin3d-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" width="100%" height={h}>
            <polyline points={line} fill="none" stroke={col} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
    );
}

export default function Twin3DPanel({ propertyId, sceneUnit, onClose }) {
    const { t } = useTranslation();
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!propertyId || !sceneUnit?.id) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        setDetail(null);
        v3Api.twin.unit(propertyId, sceneUnit.id)
            .then((res) => { if (!cancelled) setDetail(res.data); })
            .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message || t('Failed to load unit')); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [propertyId, sceneUnit?.id, t]);

    const kpis = detail?.kpis || sceneUnit.kpis || {};
    const trend = Number(kpis.yoy_growth_pct) || 0;
    const positive = trend >= 0;
    const healthKey = sceneUnit.status || 'occupied';
    const health = HEALTH[healthKey] || HEALTH.occupied;

    return (
        <div className="twin3d-panel">
            <div className="twin3d-panel-head">
                <div>
                    <span className="twin3d-eyebrow">{t('STORE INSIGHTS')}</span>
                    <h2 className="twin3d-panel-title">{sceneUnit.storeName}</h2>
                    <div className="twin3d-panel-sub">
                        <span>{sceneUnit.unitCode}</span>
                        <span className="twin3d-health" style={{ color: health.color }}>
                            <span className="twin3d-health-dot" style={{ background: health.color }} />
                            {health.label}
                        </span>
                    </div>
                </div>
                <button type="button" className="twin3d-panel-close" onClick={onClose} aria-label={t('Close')}>
                    <X size={18} />
                </button>
            </div>

            {loading && <div className="twin3d-panel-loading">{t('Loading…')}</div>}
            {error && <div className="twin3d-panel-error">{error}</div>}

            <div className="twin3d-bento">
                <div className="twin3d-cell twin3d-cell-hero">
                    <div className="twin3d-cell-label">{t('OCR %')}</div>
                    <div className="twin3d-hero-val">{fmtPct(kpis.ocr_pct)}</div>
                    <div className="twin3d-hero-row">
                        <span className={'twin3d-trend ' + (positive ? 'twin3d-trend-up' : 'twin3d-trend-dn')}>
                            {positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                            <span>{positive ? '+' : ''}{trend.toFixed(1)}%</span>
                        </span>
                        <span className="twin3d-trend-cap">{t('YoY')}</span>
                    </div>
                    <Sparkline data={detail?.kpi_history?.map((p) => p.value)} positive={positive} />
                </div>

                <div className="twin3d-cell">
                    <div className="twin3d-cell-label">{t('Footfall share')}</div>
                    <div className="twin3d-cell-val">{fmtPct(kpis.footfall_share * 100)}</div>
                    <div className="twin3d-cell-foot">{t('of property')}</div>
                </div>

                <div className="twin3d-cell">
                    <div className="twin3d-cell-label">{t('Anomalies')}</div>
                    <div className="twin3d-cell-val">
                        {kpis.anomaly_count > 0 ? (
                            <span className="twin3d-anomaly">
                                <AlertTriangle size={16} /> {kpis.anomaly_count}
                            </span>
                        ) : '0'}
                    </div>
                    <div className="twin3d-cell-foot">{t('open')}</div>
                </div>

                <div className="twin3d-cell">
                    <div className="twin3d-cell-label">{t('Total Sales')}</div>
                    <div className="twin3d-cell-val twin3d-cell-val-sm">{fmtSales(kpis.total_sales)}</div>
                    <div className="twin3d-cell-foot">{t('YTD')}</div>
                </div>

                <div className="twin3d-cell">
                    <div className="twin3d-cell-label">{t('Size')}</div>
                    <div className="twin3d-cell-val">{fmtInt(sceneUnit.squareFootage)}</div>
                    <div className="twin3d-cell-foot">{t('sq ft')}</div>
                </div>

                <div className="twin3d-cell">
                    <div className="twin3d-cell-label">{t('Lease')}</div>
                    <div className="twin3d-cell-val twin3d-cell-val-sm">
                        {detail?.lease?.expires_at
                            ? new Date(detail.lease.expires_at).toLocaleDateString()
                            : '—'}
                    </div>
                    <div className="twin3d-cell-foot">{t('expires')}</div>
                </div>

                <div className="twin3d-cell">
                    <div className="twin3d-cell-label">{t('Recent transactions')}</div>
                    <div className="twin3d-cell-val">{fmtInt(detail?.recent_transactions?.length)}</div>
                    <div className="twin3d-cell-foot">{t('logged')}</div>
                </div>
            </div>
        </div>
    );
}
